/**
 * The five places a parent goes, always on screen.
 *
 * Along the bottom on a phone, where a thumb is; along the top on anything
 * wider, where a mouse expects it. The same buttons either way, so nobody has
 * to learn two layouts.
 *
 * Icons AND words. An icon alone is a guessing game for a parent opening the
 * app once a week, and the words are what a screen reader says anyway.
 */

import { useEffect, useState } from 'react';
import type { Tab } from './Home';
import { NAV_ICONS } from '../brand/navIcons';

const ITEMS: ReadonlyArray<{ tab: Tab; label: string; icon: string }> = [
  // Living Valley icons (src/brand/navIcons.ts): each has a soft filled area
  // in currentColor, so the chosen tab lights up whole in sun gold.
  { tab: 'home', label: 'Home', icon: NAV_ICONS.home },
  { tab: 'money', label: 'Money', icon: NAV_ICONS.money },
  { tab: 'goals', label: 'Goals', icon: NAV_ICONS.goals },
  { tab: 'family', label: 'Family', icon: NAV_ICONS.family },
  { tab: 'settings', label: 'Settings', icon: NAV_ICONS.settings },
];

export function ParentNav({
  tab,
  onTab,
  badges,
}: {
  tab: Tab;
  onTab: (tab: Tab) => void;
  /** A dot on a section that has something waiting for a parent. */
  badges?: Partial<Record<Tab, boolean | number>>;
}) {
  const atEnd = useScrolledToEnd();
  return (
    <>
      {/* A phone: a solid floor under the bar, so nothing scrolls along
          behind it -- the page ends above it instead (living-valley.css 31). */}
      <div className="parent-nav-floor" aria-hidden="true" />
    <nav className="parent-nav" aria-label="Sections">
      {/* A phone: Privacy and Terms as a tab on top of the bar, only once the
          page has been scrolled to its end, instead of a long footer. */}
      <p className={atEnd ? 'parent-nav-legal shown' : 'parent-nav-legal'}>
        <a href="/privacy">Privacy</a> &middot; <a href="/terms">Terms</a>
      </p>
      {ITEMS.map((item) => (
        <button
          key={item.tab}
          type="button"
          className={item.tab === tab ? 'parent-nav-item on' : 'parent-nav-item'}
          aria-current={item.tab === tab ? 'page' : undefined}
          onClick={() => onTab(item.tab)}
        >
          <span className="parent-nav-icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              dangerouslySetInnerHTML={{ __html: item.icon }}
            />
            {typeof badges?.[item.tab] === 'number' && badges[item.tab] ? (
              <span className="parent-nav-count" aria-label={`${badges[item.tab]} waiting`}>
                {badges[item.tab]}
              </span>
            ) : badges?.[item.tab] ? (
              <span className="parent-nav-badge" />
            ) : null}
          </span>
          <span className="parent-nav-label">{item.label}</span>
        </button>
      ))}
    </nav>
    </>
  );
}

/** True once the page is scrolled to (near) its end, or cannot scroll at all. */
function useScrolledToEnd(): boolean {
  const [atEnd, setAtEnd] = useState(false);
  useEffect(() => {
    const check = () => {
      // The titled pages scroll inside their own container (living-valley.css
      // 36); Home scrolls the window.
      const page = document.querySelector<HTMLElement>('.sections > .page');
      if (page && getComputedStyle(page).overflowY === 'auto') {
        setAtEnd(page.scrollTop + page.clientHeight >= page.scrollHeight - 24);
        return;
      }
      const root = document.documentElement;
      setAtEnd(window.innerHeight + window.scrollY >= root.scrollHeight - 24);
    };
    check();
    // Capture: a scroll inside the page does not bubble to the window.
    window.addEventListener('scroll', check, { passive: true, capture: true });
    window.addEventListener('resize', check);
    // The page grows and shrinks under the bar as tabs and lists change.
    const watch = typeof ResizeObserver === 'function' ? new ResizeObserver(check) : null;
    watch?.observe(document.body);
    return () => {
      window.removeEventListener('scroll', check, { capture: true });
      window.removeEventListener('resize', check);
      watch?.disconnect();
    };
  }, []);
  return atEnd;
}
